import { Box, Button, RecordingStatus } from '@ouster/webviz-react-components';

import { isNumber } from 'lodash';
import { useEffect, useState } from 'react';
import { RiArchiveStackLine } from 'react-icons/ri';
import useChakraHook from 'src/hooks/useChakraHook';

import DashboardCard from './DashboardCard';
import { useSupportedRegions } from './useAdminDashboard';
import userLookupStyle from './userLookup.module.css';
import { useStoreActions, useStoreState } from '../../reducers';

// Utility to check if driveId is purely numeric
const isNumericDriveId = (
  value: number | string | undefined
): value is number => {
  if (typeof value === 'number') return true;
  if (typeof value === 'string') return /^\d+$/.test(value);
  return false;
};

// Utility to check if driveId is UUID-style (mixed alphanumeric)
const isUUIDStyleDriveId = (
  value: number | string | undefined
): value is string => {
  if (typeof value === 'string' && !/^\d+$/.test(value)) return true;
  return false;
};

export default function RecordingLookUpSearch() {
  const [driveId, setDriveId] = useState<number | string | undefined>(
    undefined
  );

  const { replicateDrive } = useStoreActions((actions) => actions.drive);

  const { getRecordingInfoById, clearRecordingLookup, reprocessAnyRecording } =
    useStoreActions((actions) => actions.adminDashboard);

  const { recordingLookupResult } = useStoreState(
    (storeState) => storeState.adminDashboard
  );
  const { data: supportedRegions = [] } = useSupportedRegions();

  const { showToast } = useChakraHook();

  // Determine which regions need replication
  const regionsNeedingReplication = supportedRegions.filter(
    (region) => !recordingLookupResult?.regions?.includes(region)
  );

  useEffect(() => {
    if (driveId === undefined) {
      clearRecordingLookup();
    }
  }, [driveId, clearRecordingLookup]);

  const handleReplicate = async (targetRegion: string) => {
    const orgId = recordingLookupResult?.organization_id;
    const recordingId = recordingLookupResult?.id;
    if (driveId && orgId && recordingId) {
      try {
        await replicateDrive({
          orgId,
          driveId: recordingId,
          targetRegion,
        });
        showToast({
          title: 'Replicated recording request submitted',
          description:
            'Your recording is being replicated, please check back later.',
          status: 'success',
        });
      } catch (error) {
        showToast({
          title: 'Failed to replicate recording',
          description: `${error}`,
          status: 'error',
        });
      }
    }
  };

  const handleSearch = async () => {
    if (driveId) {
      try {
        if (isNumericDriveId(driveId)) {
          // numeric drive id - convert to number if it's a string
          const numericId =
            typeof driveId === 'string' ? Number(driveId) : driveId;
          await getRecordingInfoById({ driveId: numericId, isPublic: false });
        } else if (isUUIDStyleDriveId(driveId)) {
          // UUID-style drive id - pass as string
          await getRecordingInfoById({ driveId, isPublic: true });
        }
      } catch (error) {
        console.error('Search failed:', error);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleReprocess = async () => {
    const recordingId = recordingLookupResult?.id;
    if (isNumber(recordingId)) {
      try {
        await reprocessAnyRecording({ drive_id: recordingId });

        showToast({
          title: 'Successfully reprocessed recording',
          description: '',
          status: 'success',
        });
      } catch (error) {
        showToast({
          title: 'Failed to reprocess recording',
          description: `${error}`,
          status: 'error',
        });
      }
    }
  };

  const searchContent = (
    <Box display="grid" gap={8}>
      <Box
        display="flex"
        gap={2}
        alignItems="center"
        mt={3}
        data-testid="recording-lookup-search-box"
      >
        <input
          placeholder="Enter drive ID"
          value={driveId ?? ''}
          onChange={({ target: { value } }) => {
            if (value === '') {
              setDriveId(undefined);
            } else {
              // Keep as string to preserve UUID format, but allow numeric strings too
              setDriveId(value);
            }
          }}
          type="text"
          onKeyDown={handleKeyPress}
          className={userLookupStyle.inputField}
          data-testid="recording-lookup-search-input"
        />
        <Button
          variant="outline"
          onClick={handleSearch}
          isDisabled={!driveId}
          data-testid="recording-lookup-search-btn"
        >
          Search
        </Button>
      </Box>

      {recordingLookupResult && (
        <Box
          display="grid"
          gap={4}
          pt={8}
          borderTop="1px solid"
          borderColor="whiteAlpha.400"
        >
          <Button
            colorScheme="gray"
            onClick={handleReprocess}
            isDisabled={!driveId}
            data-testid="recording-lookup-reprocess-btn"
          >
            Reprocess Recording
          </Button>
          {recordingLookupResult.osf_generation_status ===
            RecordingStatus.Finished &&
            regionsNeedingReplication.map((region) => (
              <Button
                key={region}
                colorScheme="gray"
                onClick={() => handleReplicate(region)}
                isDisabled={!driveId}
                data-testid={`recording-lookup-replicate-${region}-btn`}
              >
                Replicate to {region} region
              </Button>
            ))}
        </Box>
      )}
    </Box>
  );

  return (
    <DashboardCard
      icon={<RiArchiveStackLine size={18} />}
      title="Search Recording"
      description="Enter a drive ID to search for recording information"
      data-testid="recording-lookup-search-card"
      style={{ alignContent: 'start' }}
    >
      {searchContent}
    </DashboardCard>
  );
}
